/**
 * Builder for dosimetry summary pages (Page 2+).
 *
 * Separated from the component file so that Vite Fast Refresh
 * can hot-reload the components without being blocked by the
 * non-component `buildDosimetrySummaryPages` export.
 */

import type { ReactElement } from 'react';

import { DosimetrySummaryPage } from './dosimetry-report-page2';
import type { DosimetryStateSnapshot } from '../../hooks/use-dosimetry-state';

/** Max PV blocks per page (conservative — each ~250px, content area ~980px). */
export const PVS_PER_PAGE = 3;

/**
 * Build one or more `<DosimetrySummaryPage>` elements for the dosimetry summary section.
 *
 * @param state           Dosimetry state snapshot
 * @param startPageNumber Page number for the first summary page
 * @param totalPages      Total page count (for footer)
 * @returns Array of ReportPage elements
 */
export function buildDosimetrySummaryPages(
    state: DosimetryStateSnapshot,
    startPageNumber: number,
    totalPages: number
): ReactElement[] {
    const pvsWithDose = state.perfusedVolumes.filter((pv) => state.doseTable(pv.id) != null);

    // No PVs with dose tables — show empty state
    if (pvsWithDose.length === 0) {
        return [
            <DosimetrySummaryPage
                key="summary-empty"
                pvs={[]}
                state={state}
                pageNumber={startPageNumber}
                totalPages={totalPages}
                empty
            />,
        ];
    }

    // Chunk PVs into pages
    const pages: ReactElement[] = [];
    for (let i = 0; i < pvsWithDose.length; i += PVS_PER_PAGE) {
        const chunk = pvsWithDose.slice(i, i + PVS_PER_PAGE);
        const pageIdx = Math.floor(i / PVS_PER_PAGE);
        pages.push(
            <DosimetrySummaryPage
                key={`summary-${pageIdx}`}
                pvs={chunk}
                state={state}
                pageNumber={startPageNumber + pageIdx}
                totalPages={totalPages}
            />
        );
    }

    return pages;
}
